import { MessageOutlined } from "@ant-design/icons"
import React, {
  Suspense,
  lazy,
  useContext,
  useState,
  useSyncExternalStore
} from "react"
import { useTranslation } from "react-i18next"
import { ReactReduxContext } from "react-redux"

import type { BulkBarAction } from "../../../components/Table/BulkActionBar.tsx"
import type { RootState } from "../../../store.ts"
import type { CampaignAudience } from "../types/campaigns.types.ts"

const BulkSendWizard = lazy(() => import("./BulkSendWizard.tsx"))

const noSubscription = () => () => undefined

// Bulk bars are also rendered on their own, without a store around them.
const useMaySendBulk = () => {
  const store = useContext(ReactReduxContext)?.store
  return useSyncExternalStore(
    store?.subscribe ?? noSubscription,
    () =>
      !!(store?.getState() as RootState | undefined)?.auth?.permissions?.codes
        ?.communication?.messages?.communication_bulk_send
  )
}

export const useBulkCommunicate = (
  audience: CampaignAudience,
  ids: string[],
  filtersUsed?: string[]
): { action: BulkBarAction | null; wizard: React.ReactNode } => {
  const { t } = useTranslation()
  const maySendBulk = useMaySendBulk()
  const [open, setOpen] = useState(false)
  if (!maySendBulk) return { action: null, wizard: null }
  return {
    action: {
      key: "communicate",
      label: t("communication.bulk.communicate"),
      icon: <MessageOutlined aria-hidden />,
      onClick: () => setOpen(true)
    },
    wizard: open ? (
      <Suspense fallback={null}>
        <BulkSendWizard
          open
          audience={audience}
          ids={ids}
          filtersUsed={filtersUsed}
          onClose={() => setOpen(false)}
        />
      </Suspense>
    ) : null
  }
}
